import { DialogType } from '@tableau/widgets';
import React from 'react';

import { ActionsMenuItem } from '~common/react/components/SharedWidgetsActionMenu';
import { openReactConfirmationDialog } from '~commonInfra/dialogs/ReactConfirmationDialog';
import { ModalInstance, ReactModalOptions, ReactModalService } from '~commonInfra/dialogs/ReactModalService';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { Formatters, Messages } from '~src/codegen/Localize';
import { ActionBar, ActionBarButton } from '~src/common/react/components/ActionBar';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { L10n } from '~src/ts/L10n/L10n';
import { IPublishedConnectionDetailInfo } from '~src/ts/models/IPublishedConnectionInfo';
import { IPublishedConnectionTaskInfo } from '~src/ts/models/IPublishedConnectionTaskInfo';
import { PublishedConnectionCreateTaskDialogContent } from './PublishedConnectionCreateTaskDialogContent';
import { PublishedConnectionEditScheduleDialogContent } from './PublishedConnectionEditScheduleDialogContent';

function editScheduleDialog(tasks: ReadonlyArray<IPublishedConnectionTaskInfo>, dismiss: () => void): ReactModalOptions {
  return {
    type: DialogType.NormalWithTitleBar,
    title: Messages.changeSubscriptionsFrequency(),
    dialogContent: <PublishedConnectionEditScheduleDialogContent tasks={tasks} dismiss={dismiss}/>,
    showCloseButton: true,
    dialogWidth: TabDialogWidth.width500px,
    shouldRecenterOnUpdate: true
  };
}

function openEditScheduleDialog(tasks: ReadonlyArray<IPublishedConnectionTaskInfo>): ng.IPromise<any> {
  let modalInstance: ModalInstance;
  const dismiss = () => modalInstance.dismiss();
  modalInstance = ReactModalService.open(editScheduleDialog(tasks, dismiss));
  return modalInstance.result.catch(reason => {
  });
}

function createTaskDialog(publishedConnectionInfo: IPublishedConnectionDetailInfo, dismiss: () => void): ReactModalOptions {
  return {
    type: DialogType.NormalWithTitleBar,
    title: Messages.schedulePublishedConnectionTask(),
    dialogContent: (
      <PublishedConnectionCreateTaskDialogContent
        publishedConnectionInfo={publishedConnectionInfo}
        dismiss={dismiss}/>
    ),
    showCloseButton: true,
    dialogWidth: TabDialogWidth.width700px,
    shouldRecenterOnUpdate: true
  };
}

function openCreateTaskDialog(publishedConnectionInfo: IPublishedConnectionDetailInfo): ng.IPromise<any> {
  let modalInstance: ModalInstance;
  const dismiss = () => modalInstance.dismiss();
  modalInstance = ReactModalService.open(createTaskDialog(publishedConnectionInfo, dismiss));
  return modalInstance.result.catch(reason => {
  });
}

function editSchedulesDialog(taskInfo: IPublishedConnectionTaskInfo, dismiss: () => void): ReactModalOptions {
  return {
    type: DialogType.NormalWithTitleBar,
    title: Messages.editTask(),
    dialogContent: <PublishedConnectionCreateTaskDialogContent taskInfo={taskInfo} dismiss={dismiss}/>,
    showCloseButton: true,
    dialogWidth: 900,
    shouldRecenterOnUpdate: true
  };
}

function openEditTaskDialog(taskInfo: IPublishedConnectionTaskInfo): ng.IPromise<any> {
  let modalInstance: ModalInstance;
  const dismiss = () => modalInstance.dismiss();
  modalInstance = ReactModalService.open(editSchedulesDialog(taskInfo, dismiss));
  return modalInstance.result.catch(reason => {
  });
}

function deleteAction(tasks: ReadonlyArray<IPublishedConnectionTaskInfo>) {
  //TODO: call saga/api to delete
  return Promise.resolve({});
}

function openDeleteTasksDialog(tasks: ReadonlyArray<IPublishedConnectionTaskInfo>): Promise<any> {
  let description: string;
  if (tasks.length === 1) {
    const task = tasks[0];
    description = Messages.confirmDeletePublishedConnectionTask({ TASK_NAME: L10n.elideIfLong(task.name) });
  } else {
    description = Messages.confirmDeletePublishedConnectionTasks({
      ITEM_COUNT_LOC: Formatters.formatCount(tasks.length)
    });
  }

  const confirmationDialog = openReactConfirmationDialog<ReadonlyArray<IPublishedConnectionTaskInfo>, IResult>({
    title: Messages.deleteMessage(),
    message: description,
    paramsForAction: tasks,
    actionName: Messages.confirmDeleteButton({
      ITEM_COUNT: tasks.length,
      ITEM_COUNT_LOC: Formatters.formatCount(tasks.length)
    }),
    isActionDelete: true,
    dialogWidth: TabDialogWidth.width375px,
    action: deleteAction
  });
  return Promise.resolve(confirmationDialog.result);
}

function createActionButtons(publishedConnectionInfo: IPublishedConnectionDetailInfo): ReadonlyArray<ActionBarButton> {
  return [{
    label: Messages.newTask(),
    execute: () => {
      // open create task dialog
      openCreateTaskDialog(publishedConnectionInfo);
    },
  }];
}

function editScheduleAction(
  tasks: ReadonlyArray<IPublishedConnectionTaskInfo>
): ActionsMenuItem {
  return {
    label: Messages.editSchedules(),
    execute: () => {
      openEditScheduleDialog(tasks);
    }
  };
}

function editTaskAction(
  tasks: ReadonlyArray<IPublishedConnectionTaskInfo>
): ActionsMenuItem {
  return {
    label: Messages.editTask_action(),
    execute: () => {
      if (tasks.length === 1) {
        openEditTaskDialog(tasks[0]);
      }
    }
  };
}

function deleteTasksAction(
  tasks: ReadonlyArray<IPublishedConnectionTaskInfo>
): ActionsMenuItem {
  return {
    label: Messages.delete_action(),
    execute: () => {
      openDeleteTasksDialog(tasks);
    }
  };
}

export const PublishedConnectionTasksActionBar: React.FC<{
  clearTasks: () => void;
  publishedConnectionInfo: IPublishedConnectionDetailInfo;
  selectAllTasks: () => void;
  selectedTasks: ReadonlyArray<IPublishedConnectionTaskInfo>;
}> = (
  {
    clearTasks,
    publishedConnectionInfo,
    selectAllTasks,
    selectedTasks,
  }) => {

  const actionButtons = React.useMemo(
    () => createActionButtons(publishedConnectionInfo),
    [publishedConnectionInfo]
  );

  const actionsMenuItems = React.useMemo(
    (): ReadonlyArray<ActionsMenuItem> => {
      return [
        selectedTasks.length > 1
          ? editScheduleAction(selectedTasks)
          : editTaskAction(selectedTasks),
        deleteTasksAction(selectedTasks)
      ];
    },
    [selectedTasks]
  );

  //TODO: check for permissions
  return (
    <ActionBar
      actionButtons={actionButtons}
      actionsMenuItems={actionsMenuItems}
      anyActionsAllowed={true}
      selectionSize={selectedTasks.length}
      selectAll={selectAllTasks}
      clear={clearTasks}
    />
  );
};
PublishedConnectionTasksActionBar.displayName = 'PublishedConnectionTasksActionBar';
